import { useRouter } from 'next/router'
import type { JSX } from 'react'
import { useEffect, useState } from 'react'
import type { Control } from 'react-hook-form'
import { useFormContext } from 'react-hook-form'

import {
    DATE_INPUT_FORMAT,
    validateAgeIsExactly,
} from '@components/input/date-input'
import { Spinner } from '@components/spinner'
import { Fieldset } from '@contentful-components/fieldset'
import { Submit } from '@contentful-components/forms/submit'
import { PageLoadingState } from '@contentful-components/loading-state'
import { PageMeta } from '@contentful-components/page-meta'
import { renderSection } from '@contentful-components/section-resolver'
import type { FieldsetFragment } from '@graphql/fragments/FieldsetFragment.graphql'
import type { SubmitFragment } from '@graphql/fragments/SubmitFragment.graphql'
import { CustomFieldsetId, useFormPage } from '@graphql/hooks/form-page'
import { EMPTY_COLLECTION } from '@graphql/placeholders'
import type { KycFormValues } from '@lib/utils/kyc'
import { suspense } from '@lib/utils/suspense'
import type { PageTemplateProps } from '@page-templates/generic-page'
import styles from '@page-templates/page.module.scss'

/**
 * A conditional form based on the user's date of birth.
 */
export const ConditionalKycFormPage = suspense<Props>(
    ({ slug, 'data-testid': testId, meta: metaProps }) => {
        const [updatedFragments, setUpdatedFragments] = useState<
            FieldsetFragment[]
        >([])

        const { watch } = useFormContext<KycFormValues>()
        const watchedDoB = watch('personalInfo.dateOfBirth')
        const [prevDoB, setPrevDoB] = useState<string | undefined>(undefined)

        const {
            fetching,
            page: {
                meta,
                sections: { items: fragments } = EMPTY_COLLECTION,
                submit,
            },
        } = useFormPage({ slug })

        useEffect(() => {
            // Check if DoB has changed
            if (watchedDoB !== prevDoB) {
                // Update the previous DoB
                setPrevDoB(watchedDoB)
                // Filter fragments that are of type FieldsetFragment
                const fieldsetFragments = fragments.filter(
                    (fragment: any): fragment is FieldsetFragment =>
                        fragment.type === 'Fieldset',
                )
                const newFragments = filterFragmentsByAge(
                    fieldsetFragments,
                    watchedDoB && watchedDoB.length === DATE_INPUT_FORMAT.length
                        ? // Only check date of birth when they filled out the entire date.
                          validateAgeIsExactly(watchedDoB, 18)
                        : // Else assume they are over 18
                          (false as boolean),
                )

                // Update form fragments based on if user is 18 or not.
                setUpdatedFragments(newFragments as FieldsetFragment[])
            }
        }, [watchedDoB, fragments])

        const filterFragmentsByAge = (
            fragments: FieldsetFragment[],
            is18: boolean,
        ): FieldsetFragment[] => {
            return fragments
                .map((fragment) => {
                    if (!fragment?.customId) return fragment // Skip if no customId

                    if (
                        is18 &&
                        fragment.customId ===
                            CustomFieldsetId.KycEnterLastFourSsn
                    ) {
                        return null // Remove fragment for last four SSN if the user is 18
                    }

                    if (
                        !is18 &&
                        fragment.customId === CustomFieldsetId.KycEnterFullSsn
                    ) {
                        return null // Remove full SSN fragment if the user is under 18
                    }

                    return fragment // Keep other fragments unchanged
                })
                .flatMap((f) => (f ? [f] : [])) // Remove any null values
        }

        const {
            query: { next },
        } = useRouter()

        return (
            <div className={styles.page} data-testid={testId}>
                {meta && (
                    <PageMeta
                        id={meta.sys.id}
                        name={metaProps?.name ?? slug}
                        meta={meta}
                        {...metaProps}
                    />
                )}

                {fetching ? (
                    <PageLoadingState />
                ) : (
                    <>
                        {updatedFragments.map((s) =>
                            s.type === 'Fieldset' ? (
                                <FieldsetWrapper
                                    key={s.customId}
                                    next={next as string}
                                    submit={submit}
                                    {...s}
                                />
                            ) : (
                                renderSection(s)
                            ),
                        )}
                    </>
                )}
            </div>
        )
    },
    <Spinner size='large' />,
)

const FieldsetWrapper = suspense<
    FieldsetFragment & {
        submit?: SubmitFragment
        next?: string
    } & JSX.IntrinsicAttributes
>(
    ({ customId, next, submit, section }) => (
        <Fieldset
            section={section}
            customId={customId as CustomFieldsetId}
            key={customId}
            next={typeof next === 'string' ? next : undefined}
            renderSubmit={(control) => (
                <Submit
                    {...submit}
                    order={submit?.order === 'reverse' ? 'reverse' : 'default'}
                    control={control as Control}
                />
            )}
        />
    ),
    <Spinner size='large' />,
)

type Props = PageTemplateProps & {
    next?: string
} & JSX.IntrinsicAttributes
